import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '../../../ui/Form';
import { CustomFormField } from '../../../app/CustomFormField';
import { FormFieldTypes } from '@hero/web-types';
import { Button } from '../../../ui/Button';
import { Spinner } from '../../../app/Spinner';
import { SelectItem } from '../../../ui/Select';
import { FormControl } from '../../../ui/Form';
import { DriverNationalitiesSelector } from '../../Driver';
import { FileUploader } from '../../../app/File/FileUploader/FileUploader';
import { fileToBase64 } from '@hero/web-utils';
import { InfoIcon } from '@hero/web-icons';
import { LogistiFieldBadge } from '../../../app/LogistiFieldBadge';
import { InputLabel } from '../../../ui/Input';
import { Checkbox } from '../../../ui/Checkbox';
import type { UseMemberFormHook } from '@hero/web-hooks';
import type { MemberType } from '@hero/schema';

const ACCEPTED_IMAGE_TYPES: Record<string, string[]> = {
  'image/jpeg': [],
  'image/png': [],
  'image/webp': [],
  'application/pdf': [],
};

const CUSTOMER_TYPES: MemberType[] = ['admin', 'dispatcher', 'employee'];
const CARRIER_TYPES: MemberType[] = ['admin', 'dispatcher', 'driver'];

export interface MemberFormProps {
  /** Form hook from useMemberForm(); required. */
  formHook: UseMemberFormHook;
  isLoading?: boolean;
  hideSubmitButton?: boolean;
  displayForUser?: 'customer' | 'carrier';
  /** Lock the member type so user can't change it. */
  forceType?: MemberType;
  /**
   * Lock the phone number field — pass `isPhoneLocked` from
   * `useMemberCreateAdapter`: true when the member is you (your number is
   * your sign-in).
   */
  isPhoneLocked?: boolean;
  /** Pass `phoneChange` from `useMemberCreateAdapter` — set while the number is being changed. */
  phoneChange?: {
    oldPhone: string;
    keepOldUserOnOrg: boolean;
    setKeepOldUserOnOrg: (keep: boolean) => void;
  };
  /** Pass `accountRename` from `useMemberCreateAdapter` — set while a shared account's name would differ. */
  accountRename?: {
    accountName: string;
    renameAccount: boolean;
    setRenameAccount: (rename: boolean) => void;
  };
}

export function MemberForm({
  formHook,
  isLoading = false,
  hideSubmitButton = false,
  displayForUser = 'customer',
  forceType,
  isPhoneLocked = false,
  phoneChange,
  accountRename,
}: MemberFormProps) {
  const { t } = useTranslation(['m-member', 'c-main']);
  const isEdit = formHook.isEdit;
  const userType = formHook.watch('type');
  const isDriver = userType === 'driver';
  const availableTypes = displayForUser === 'carrier' ? CARRIER_TYPES : CUSTOMER_TYPES;
  const isTypeLocked = !!forceType || isEdit;

  useEffect(() => {
    if (forceType && userType !== forceType) {
      formHook.setValue('type', forceType);
    }
  }, [forceType, userType, formHook]);

  const [licenseFiles, setLicenseFiles] = useState<File[]>([]);
  const [idImageFiles, setIdImageFiles] = useState<File[]>([]);

  const existingLicenseUrl = (() => {
    const v = formHook.watch('driver.driverLicenseImage');
    const url = v?.url ?? v?.upload ?? '';
    return typeof url === 'string' && url.startsWith('http') ? url : '';
  })();
  const existingIdImageUrl = (() => {
    const v = formHook.watch('driver.idImage');
    const url = v?.url ?? v?.upload ?? '';
    return typeof url === 'string' && url.startsWith('http') ? url : '';
  })();

  const handleFileChange = async (files: File[], setFiles: (f: File[]) => void, field: any) => {
    setFiles(files);
    if (files[0]) {
      const base64 = await fileToBase64(files[0]);
      field.onChange({ upload: base64 });
    } else {
      field.onChange(undefined);
    }
  };

  return (
    <Form {...formHook}>
      <div className="space-y-4">
        <CustomFormField
          filedType={FormFieldTypes.SELECT}
          control={formHook.control}
          name="type"
          placeholder={t('form.type.placeholder')}
          label={t('form.type.title')}
          disabled={isTypeLocked}>
          {availableTypes.map(type => (
            <SelectItem key={type} value={type} description={t(`types.${type}.description`)}>
              {t(`types.${type}.title`)}
            </SelectItem>
          ))}
        </CustomFormField>

        <div>
          <CustomFormField
            control={formHook.control}
            name="name"
            filedType={FormFieldTypes.INPUT}
            placeholder={t('form.name.placeholder')}
            label={t('form.name.title')}
            required
          />
          {accountRename ? (
            <label className="mt-2 flex items-start gap-2 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={accountRename.renameAccount}
                onCheckedChange={checked => accountRename.setRenameAccount(checked === true)}
                disabled={isLoading}
              />
              <span className="space-y-0.5">
                <span className="block">
                  {t('form.name.rename_account.title', { name: accountRename.accountName })}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {t('form.name.rename_account.hint')}
                </span>
              </span>
            </label>
          ) : null}
        </div>

        <div>
          <CustomFormField
            control={formHook.control}
            filedType={FormFieldTypes.PHONE_INPUT}
            name="phone"
            placeholder={t('form.phone_number.placeholder')}
            label={t('form.phone_number.title')}
            disabled={isPhoneLocked}
            required
          />
          {isPhoneLocked ? (
            <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-600">
              <InfoIcon className="size-3.5 shrink-0 mt-0.5" />
              <span>{t('form.phone_number.locked_hint')}</span>
            </p>
          ) : (
            <p className="mt-1.5 flex items-start gap-1.5 text-xs text-muted-foreground">
              <InfoIcon className="size-3.5 shrink-0 mt-0.5" />
              <span>{t('form.phone_number.login_hint')}</span>
            </p>
          )}
          {phoneChange ? (
            <label className="mt-2 flex items-start gap-2 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={phoneChange.keepOldUserOnOrg}
                onCheckedChange={checked => phoneChange.setKeepOldUserOnOrg(checked === true)}
                disabled={isLoading}
              />
              <span className="space-y-0.5">
                <span className="block">
                  {t('form.phone_number.keep_old_on_org.title', { phone: phoneChange.oldPhone })}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {t('form.phone_number.keep_old_on_org.hint')}
                </span>
              </span>
            </label>
          ) : null}
        </div>

        {isDriver && (
          <>
            <LogistiFieldRow label={t('form.full_name.title')}>
              <CustomFormField
                control={formHook.control}
                name="driver.fullName"
                filedType={FormFieldTypes.INPUT}
                placeholder={t('form.full_name.placeholder')}
              />
            </LogistiFieldRow>

            <LogistiFieldRow label={t('form.driver_id.title')}>
              <CustomFormField
                control={formHook.control}
                name="driver.id"
                filedType={FormFieldTypes.INPUT}
                placeholder={t('form.driver_id.placeholder')}
              />
            </LogistiFieldRow>

            {/* Issue number — the one Logisti field a waybill cannot be
                issued without, and the one nothing else in Hero needs. */}
            <LogistiFieldRow label={t('form.id_issue_number.title')} variant="required">
              <CustomFormField
                control={formHook.control}
                name="driver.idIssueNumber"
                filedType={FormFieldTypes.INPUT}
                placeholder={t('form.id_issue_number.placeholder')}
              />
            </LogistiFieldRow>

            <LogistiFieldRow label={t('form.nationality.title')}>
              <DriverNationalitiesSelector
                control={formHook.control}
                name="driver.nationality"
                hideLabel
              />
            </LogistiFieldRow>

            <LogistiFieldRow label={t('form.driver_license_image.title')}>
              <CustomFormField
                control={formHook.control}
                name="driver.driverLicenseImage"
                filedType={FormFieldTypes.SKELETON}
                renderSkeleton={(field: any) => (
                  <FormControl>
                    <FileUploader
                      files={licenseFiles.length > 0 ? licenseFiles : undefined}
                      onChange={files => handleFileChange(files, setLicenseFiles, field)}
                      acceptedFileTypes={ACCEPTED_IMAGE_TYPES}
                      label={t('form.driver_license_image.title')}
                      variant="compact"
                      existingUrls={existingLicenseUrl ? [existingLicenseUrl] : undefined}
                      status={licenseFiles.length > 0 || existingLicenseUrl ? 'success' : 'neutral'}
                    />
                  </FormControl>
                )}
              />
            </LogistiFieldRow>

            <LogistiFieldRow label={t('form.id_image.title')}>
              <CustomFormField
                control={formHook.control}
                name="driver.idImage"
                filedType={FormFieldTypes.SKELETON}
                renderSkeleton={(field: any) => (
                  <FormControl>
                    <FileUploader
                      files={idImageFiles.length > 0 ? idImageFiles : undefined}
                      onChange={files => handleFileChange(files, setIdImageFiles, field)}
                      acceptedFileTypes={ACCEPTED_IMAGE_TYPES}
                      label={t('form.id_image.title')}
                      variant="compact"
                      existingUrls={existingIdImageUrl ? [existingIdImageUrl] : undefined}
                      status={idImageFiles.length > 0 || existingIdImageUrl ? 'success' : 'neutral'}
                    />
                  </FormControl>
                )}
              />
            </LogistiFieldRow>
          </>
        )}

        {!hideSubmitButton && (
          <Button
            type="button"
            className="w-full"
            disabled={isLoading}
            onClick={() => formHook.onValidSubmit()}>
            {isLoading ? (
              <>
                <Spinner className="mr-2" />
                <span>{t('c-main:async_operations.saving')}</span>
              </>
            ) : isEdit ? (
              <span>{t('c-main:crud_buttons.update')}</span>
            ) : (
              <span>{t('c-main:crud_buttons.create')}</span>
            )}
          </Button>
        )}
      </div>
    </Form>
  );
}

/**
 * Label row that pairs an `InputLabel` with the shared
 * `LogistiFieldBadge` and renders the input below.
 *
 * Used inside MemberForm to annotate every driver-only field as
 * "Recommended for Logisti" — these fields are optional for member
 * creation (only `name` + `phone` are required) but recommended so
 * the Logisti integration can verify and dispatch the driver.
 *
 * Pass `variant="required"` for the fields a government waybill
 * genuinely cannot be issued without, so the two kinds of
 * "Logisti wants this" are visually distinguishable.
 */
function LogistiFieldRow({
  label,
  children,
  variant = 'recommended',
}: {
  label: string;
  children: React.ReactNode;
  variant?: 'required' | 'recommended';
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <InputLabel label={label} className="mb-0" />
        <LogistiFieldBadge variant={variant} />
      </div>
      {children}
    </div>
  );
}
